import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { guessCarrier } from '../lib/carrierGuess';

const CARRIERS = [
  { v: 'usps', label: 'USPS' },
  { v: 'ups', label: 'UPS' },
  { v: 'fedex', label: 'FedEx' },
  { v: 'other', label: 'Other' },
];
const STATUSES = [
  { v: 'label_created', label: 'Label created' },
  { v: 'in_transit', label: 'In transit' },
  { v: 'out_for_delivery', label: 'Out for delivery' },
  { v: 'ready_for_pickup', label: 'Ready for pickup' },
  { v: 'delivered', label: 'Delivered' },
  { v: 'exception', label: 'Exception' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.v, s.label]));
const BLANK = { tracking_number: '', carrier: 'usps', label: '', status: 'label_created', note: '' };

export default function Packages() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(BLANK);
  // True once the user picks a carrier by hand; suppresses auto-guess from then on.
  const [carrierTouched, setCarrierTouched] = useState(false);
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState('active'); // 'active' | 'archived'

  async function load() {
    const res = await apiFetch(view === 'archived' ? '/api/packages/archived' : '/api/packages');
    if (res.ok) { const d = await res.json(); setPackages(d.packages); }
  }
  useEffect(() => { load(); }, [view]);

  // Manually run the FedEx sync for all non-delivered packages, then reload.
  async function refreshAll() {
    if (refreshing) return;
    setRefreshing(true); setMsg('');
    try {
      const res = await apiFetch('/api/packages/refresh', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg(`Refreshed ${d.processed || 0} package${d.processed === 1 ? '' : 's'}` +
          (d.changed ? `, ${d.changed} updated.` : '.'));
        load();
      } else {
        setMsg(d.error || 'Refresh failed.');
      }
    } catch (e) {
      setMsg('Could not reach the server.');
    } finally {
      setRefreshing(false);
    }
  }

  async function create() {
    setMsg('');
    const res = await apiFetch('/api/packages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setForm(BLANK); setCarrierTouched(false); setShowForm(false); load();
    } else {
      const d = await res.json().catch(() => ({}));
      setMsg(d.error || 'Could not add package.');
    }
  }

  async function setArchived(id, archived, e) {
    if (e) e.stopPropagation();
    const res = await apiFetch(`/api/packages/${id}/archive`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived }),
    });
    if (res.ok) load();
  }

  async function purge(id, e) {
    if (e) e.stopPropagation();
    if (!window.confirm('Permanently delete this archived package and its history? This cannot be undone.')) return;
    const res = await apiFetch(`/api/packages/${id}`, { method: 'DELETE' });
    if (res.ok) load();
  }

  function fmtDate(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString(); } catch { return '—'; }
  }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Packages</h1>
        <p>Track shipments and log status updates.</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>{showForm ? 'New package' : 'Add a package'}</h2>
          <button className="hq-btn" onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Cancel' : 'New package'}
          </button>
        </div>
        {view === 'active' && showForm && (
          <div style={{ padding: 20 }}>
            <div className="hq-form">
              <input className="hq-input" placeholder="Tracking number" value={form.tracking_number}
                onChange={(e) => {
                  const tn = e.target.value;
                  const g = carrierTouched ? null : guessCarrier(tn);
                  setForm((f) => ({ ...f, tracking_number: tn, carrier: g || f.carrier }));
                }} />
              <select className="hq-select" value={form.carrier}
                onChange={(e) => { setCarrierTouched(true); setForm({ ...form, carrier: e.target.value }); }}>
                {CARRIERS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
              <input className="hq-input" placeholder="Label (e.g. Spring tees reorder)" value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })} />
              <select className="hq-select" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              <input className="hq-input" placeholder="Note (optional)" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })} style={{ minWidth: 220 }} />
              <button className="hq-btn" onClick={create}>Save</button>
            </div>
            {msg && <p className="hq-msg">{msg}</p>}
          </div>
        )}
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head">
          <h2>All packages</h2>
          <button className="hq-btn" onClick={refreshAll} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh all'}
          </button>
        </div>
        <table className="hq-table">
          <thead>
            <tr><th>Tracking</th><th>Carrier</th><th>Ship to</th><th>Status</th><th>Est. delivery</th><th>Latest note</th><th>Last update</th><th></th></tr>{/* @@PKG_ADDR_v57@@ */}
          </thead>
          <tbody>
            {packages.map((p) => (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/packages/${p.id}`)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{p.tracking_number}</div>
                  {p.label && <div style={{ color: 'var(--hq-muted)', fontSize: 12 }}>{p.label}</div>}
                </td>
                <td style={{ textTransform: 'uppercase' }}>{p.carrier}</td>
                <td style={{ color: 'var(--hq-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{p.destination || '—'}</td>
                <td><span className={`hq-pill hq-pkg-${p.status}`}>{STATUS_LABEL[p.status] || p.status}</span></td>
                <td>{fmtDate(p.estimated_delivery)}</td>
                <td style={{ color: 'var(--hq-muted)', fontSize: 13 }}>
                  {p.last_note ? p.last_note.slice(0, 60) : '—'}
                </td>
                <td>{fmtDate(p.last_update_at)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {view === 'archived' ? (
                    <>
                      <button className="hq-link-btn" onClick={(e) => setArchived(p.id, false, e)}>Unarchive</button>
                      <button className="hq-link-btn hq-link-danger" onClick={(e) => purge(p.id, e)}>Purge</button>
                    </>
                  ) : (
                    <button className="hq-link-btn" onClick={(e) => setArchived(p.id, true, e)}>Archive</button>
                  )}
                </td>
              </tr>
            ))}
            {packages.length === 0 && (
              <tr><td className="hq-empty" colSpan={8}>{view === 'archived' ? 'No archived packages.' : 'No packages yet.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
